import React, { FC, useCallback, useEffect } from 'react';
import {
  Control,
  useFieldArray,
  UseFormResetField,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import { CalendarIcon, CloseIcon } from '../../../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../../../components/app-formatted-message/index';
import Button from '../../../../../../components/button/index';
import { OverlayDatePicker as DatePicker } from '../../../../../../components/date-picker/index';
import { FormInput } from '../../../../../../components/input/index';
import { useFormat } from '../../../../../../hooks/index';
import { SharePlanType } from '../../../../../../types/pool-plans.types';
import { formatRemainingExercisable } from '../calculate-remaining-exercisable.util';
import { GrantCombobox, StakeholderCombobox, TableHeaderItem } from '../form-components/index';
import { FormSchema } from '../terminated-exercise-form.validation';

export type WarrantExerciseProps = {
  companyId: string;
  control: Control<FormSchema>;
  resetField: UseFormResetField<FormSchema>;
  setFormData: UseFormSetValue<FormSchema>;
};

export const WarrantExercise: FC<WarrantExerciseProps> = ({
  control,
  setFormData,
  companyId,
  resetField,
}) => {
  const { stepOne } = useWatch<FormSchema>({ control });
  const { format } = useFormat();

  const {
    fields: warrantItemsFields,
    append: warrantItemsAppend,
    remove: warrantItemsRemove,
  } = useFieldArray({
    control,
    name: 'stepOne.warrantExercise.warrantExerciseItems',
  });

  const handleRemoveWarrantItems = useCallback(
    (index: number) => {
      warrantItemsRemove(index);
    },
    [warrantItemsRemove],
  );

  const handleAppendWarrantItems = useCallback(() => {
    const defaultDate = new Date();
    warrantItemsAppend({
      stakeholder: { id: '', fullName: '' },
      grant: {
        id: '',
        grantItem: {
          plan: {
            warrantPrice: 0,
            expiryDate: defaultDate,
            conversionRatio: 1,
            pool: { shareClass: { name: '' } },
          },
        },
        exercisedCount: 0,
        vestedSharesCount: 0,
      },
      date: defaultDate,
      exercised: 0,
      returnedToPool: 0,
    });
  }, [warrantItemsAppend]);

  const handleStakeholderChange = (index: number) => {
    resetField(`stepOne.warrantExercise.warrantExerciseItems.${index}.grant`);
  };

  const calculateBalance = useCallback(
    (index: number) => {
      const item = stepOne?.warrantExercise?.warrantExerciseItems?.[index];
      const vestedShares = item?.grant?.vestedSharesCount || 0;
      const exercisedShares = item?.grant?.exercisedCount || 0;
      const exercised = item?.exercised || 0;
      const returnedToPool = item?.returnedToPool || 0;

      return formatRemainingExercisable(vestedShares, exercisedShares, exercised, returnedToPool);
    },
    [stepOne?.warrantExercise?.warrantExerciseItems],
  );

  const checkExerciseDate = useCallback(
    (index: number) => {
      const expiryDate =
        stepOne?.warrantExercise?.warrantExerciseItems?.[index]?.grant?.grantItem?.plan?.expiryDate;
      const execiseDate = stepOne?.warrantExercise?.warrantExerciseItems?.[index]?.date;

      if (expiryDate && execiseDate) {
        const isValid = new Date(expiryDate).getTime() < new Date(execiseDate).getTime();
        return isValid;
      }

      return false;
    },
    [stepOne?.warrantExercise?.warrantExerciseItems],
  );

  useEffect(() => {
    if (warrantItemsFields.length === 0) {
      handleAppendWarrantItems();
    }
  }, [handleAppendWarrantItems, warrantItemsFields.length]);

  const showRemoveButton = warrantItemsFields.length > 1;

  return (
    <>
      <div className="flex w-[1420px] flex-col rounded-lg border-[1px] border-gray-100 bg-white">
        <div
          className={twMerge(
            'grid',
            showRemoveButton
              ? 'grid-cols-[repeat(3,184px)_repeat(5,minmax(164px,1fr))_repeat(1,48px)]'
              : 'grid-cols-[repeat(3,184px)_repeat(5,minmax(164px,1fr))]',
          )}
        >
          <TableHeaderItem showTooltip text={<AppFormattedMessage id="STAKEHOLDER" />} />
          <TableHeaderItem showTooltip text={<AppFormattedMessage id="GRANT" />} />
          <TableHeaderItem showTooltip text={<AppFormattedMessage id="EXERCISE_DATE" />} />
          <TableHeaderItem showTooltip text={<AppFormattedMessage id="SHARE_CLASS" />} />
          <TableHeaderItem text={<AppFormattedMessage id="WARRANT_PRICE" />} variant="dark" />
          <TableHeaderItem text={<AppFormattedMessage id="EXERCISED_WARRANTS" />} variant="dark" />
          <TableHeaderItem text={<AppFormattedMessage id="ISSUED_SHARES" />} variant="dark" />
          <TableHeaderItem text={<AppFormattedMessage id="RETURNED_TO_POOL" />} variant="dark" />
          {showRemoveButton && <TableHeaderItem text="" />}
        </div>
        {warrantItemsFields.map((field, index) => (
          <>
            <div
              className={twMerge(
                'grid border-b-[1px] border-b-gray-100',
                showRemoveButton
                  ? 'grid-cols-[repeat(3,184px)_repeat(5,minmax(164px,1fr))_repeat(1,48px)]'
                  : 'grid-cols-[repeat(3,184px)_repeat(5,minmax(164px,1fr))]',
              )}
              key={field.id}
            >
              <div className="px-4 pb-10 pt-7">
                <StakeholderCombobox
                  companyId={companyId}
                  control={control}
                  handleStakeholderChange={() => handleStakeholderChange(index)}
                  name={`stepOne.warrantExercise.warrantExerciseItems.${index}.stakeholder`}
                  planType={SharePlanType.WARRANTS}
                />
              </div>
              <div className="px-4 pb-10 pt-7">
                <GrantCombobox
                  companyId={companyId}
                  control={control}
                  name={`stepOne.warrantExercise.warrantExerciseItems.${index}.grant`}
                  selectedGrantIds={stepOne?.warrantExercise?.warrantExerciseItems?.map(
                    (item) => item?.grant?.id || '',
                  )}
                  sharePlanType={SharePlanType.WARRANTS}
                  stakeholderId={
                    stepOne?.warrantExercise?.warrantExerciseItems?.[index]?.stakeholder?.id || ''
                  }
                />
              </div>
              <div className="px-4 pb-10 pt-7">
                <DatePicker
                  calendar={{
                    toDate:
                      stepOne?.warrantExercise?.warrantExerciseItems?.[index]?.grant?.grantItem
                        ?.plan?.expiryDate,
                  }}
                  onSelect={(date) =>
                    setFormData(`stepOne.warrantExercise.warrantExerciseItems.${index}.date`, date)
                  }
                  value={stepOne?.warrantExercise?.warrantExerciseItems?.[index]?.date}
                  wrapperClassName="w-full"
                >
                  <FormInput
                    autoComplete="off"
                    control={control}
                    disabledInput
                    icon={<CalendarIcon className="size-5" iconColor="#344054" />}
                    inputClassName="h-10"
                    name={`stepOne.warrantExercise.warrantExerciseItems.${index}.date`}
                    transform={(value) => {
                      return format(value, 'dd/MM/yyyy');
                    }}
                    wrapperClassName="w-full"
                  />
                </DatePicker>
              </div>
              <div className="flex items-center px-4 pb-10 pt-7">
                <span className="text-sm font-[450] text-gray-700">
                  {
                    stepOne?.warrantExercise?.warrantExerciseItems?.[index]?.grant?.grantItem?.plan
                      ?.pool?.shareClass?.name
                  }
                </span>
              </div>
              <div className="flex flex-col gap-2 px-4 pb-4 pt-7">
                <FormInput
                  control={control}
                  disabled={true}
                  inputClassName="h-10"
                  name={`stepOne.warrantExercise.warrantExerciseItems.${index}.grant.grantItem.plan.warrantPrice`}
                  shouldFormatNumber
                  wrapperClassName="w-full"
                />
                <span className="block max-w-[164px] overflow-hidden truncate text-ellipsis whitespace-nowrap text-xs font-[450] text-gray-600">
                  <AppFormattedMessage id="TOTAL_COST" /> $
                  {(
                    (stepOne?.warrantExercise?.warrantExerciseItems?.[index]?.grant?.grantItem?.plan
                      ?.warrantPrice || 0) *
                    (stepOne?.warrantExercise?.warrantExerciseItems?.[index]?.exercised ?? 0)
                  ).toLocaleString('en-US')}
                </span>
              </div>
              <div className="flex flex-col gap-2 px-4 pb-4 pt-7">
                <FormInput
                  control={control}
                  inputClassName="h-10"
                  name={`stepOne.warrantExercise.warrantExerciseItems.${index}.exercised`}
                  numberOnly
                  shouldDisplayZero
                  shouldFormatNumber
                  wrapperClassName="w-full"
                />
                <span className="text-xs font-[450] text-gray-600">
                  <AppFormattedMessage id="AVAILABLE" /> {calculateBalance(index)}
                </span>
              </div>
              <div className="flex flex-col gap-2 px-4 pb-4 pt-7">
                <div className="max-w-[164px] rounded-t-[4px] bg-gray-50 px-3 py-2">
                  <span className="block w-full overflow-hidden truncate text-ellipsis whitespace-nowrap text-sm font-[450] text-gray-700">
                    {(
                      (stepOne?.warrantExercise?.warrantExerciseItems?.[index]?.exercised || 0) *
                      (stepOne?.warrantExercise?.warrantExerciseItems?.[index]?.grant?.grantItem
                        ?.plan?.conversionRatio || 1)
                    ).toLocaleString('en-US')}
                  </span>
                </div>
                <span className="text-xs font-[450] text-gray-600">
                  <AppFormattedMessage id="CONVERSION" />{' '}
                  {stepOne?.warrantExercise?.warrantExerciseItems?.[index]?.grant?.grantItem?.plan
                    ?.conversionRatio || 1}
                  x
                </span>
              </div>
              <div className="flex flex-col gap-2 px-4 pb-4 pt-7">
                <FormInput
                  control={control}
                  inputClassName="h-10"
                  name={`stepOne.warrantExercise.warrantExerciseItems.${index}.returnedToPool`}
                  numberOnly
                  shouldDisplayZero
                  shouldFormatNumber
                  wrapperClassName="w-full"
                />
                <span className="text-xs font-[450] text-gray-600">
                  <AppFormattedMessage id="AVAILABLE" /> {calculateBalance(index)}
                </span>
              </div>
              {showRemoveButton && (
                <div>
                  <Button
                    className="flex h-full w-full items-center justify-center"
                    onClick={() => handleRemoveWarrantItems(index)}
                    styleType="NONE"
                  >
                    <CloseIcon />
                  </Button>
                </div>
              )}
              {checkExerciseDate(index) && (
                <span className="w-[400px] px-4 pb-4 text-xs font-[450] text-fireside-600">
                  <AppFormattedMessage id="EXERCISE_DATE_CANT_BE_LATER_THAN_EXPIRE" />
                </span>
              )}
            </div>
          </>
        ))}
      </div>

      {warrantItemsFields.length < 10 && (
        <div className="sticky bottom-[76px] z-10 w-full bg-white">
          <Button
            className="w-fit px-4 py-2 text-sm font-[450] text-brand-700 underline disabled:border-transparent disabled:bg-transparent"
            onClick={handleAppendWarrantItems}
            styleType="NONE"
          >
            + <AppFormattedMessage id="ADD_EXERCISING" />
          </Button>
        </div>
      )}
    </>
  );
};
